import axios, { InternalAxiosRequestConfig } from "axios";

import { store } from "../redux/app/store";
import { storeToken } from "../redux/features/authentication/signIn";

export interface AuthRequestConfig extends InternalAxiosRequestConfig {
  token?: string;
  _retry?: boolean;
}

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function subscribeTokenRefresh(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

function onRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

// Initialize an Axios instance
const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    "Cache-Control": "no-cache",
  },
  timeout: 10000,
});

// Access token interceptor logic
apiClient.interceptors.request.use((config: AuthRequestConfig) => {
  const token = store.getState().signedIn.token;
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  return config;
});

// Token refresh interceptor logic
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as AuthRequestConfig;

    if (!error.response || error.response.status !== 401) {
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    if (originalRequest.url?.includes("/auth/token/refresh")) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve) => {
        subscribeTokenRefresh((token) => {
          originalRequest.headers.set("Authorization", `Bearer ${token}`);
          resolve(apiClient(originalRequest));
        });
      });
    }

    isRefreshing = true;

    try {
      const response = await axios.post(
        `${API}/auth/token/refresh`,
        {},
        { withCredentials: true }
      );

      const newToken = response.data.accessToken.token;
      store.dispatch(storeToken(newToken));
      onRefreshed(newToken);

      originalRequest.headers.set("Authorization", `Bearer ${newToken}`);

      return apiClient(originalRequest);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default apiClient;
